<template>
  <BaseSection>
    <BaseSectionHeader :header-text="$t('Budget tracking')" />
    <BaseSectionBody>
      <WdsAlert warning bordered>
        {{ $t('This information is private and not shared with applicants') }}
      </WdsAlert>
      <BaseFieldGroup
        :label="$t('Internal budget')"
        :helper-text="$t('What’s the net total budget for this role?')"
        :optional="!requiredJobBudget"
      >
        <div class="tw-w-full">
          <WdsInputCurrency
            v-model="store.form.internalBudget"
            class="tw-w-full"
            :config="inputCurrencyConfig"
            :error="getErrorMessage(validationStore, 'internalBudget')"
          />
        </div>
      </BaseFieldGroup>
      <BaseFieldGroup
        v-if="projectsEnabled"
        :label="$t('Project')"
        :helper-text="$t('Which project will this job show costs against?')"
        optional
      >
        <div class="tw-w-full">
          <WdsSelect
            v-model="selectedProject"
            :list="projects"
            :placeholder="$t('Select project')"
            :load-more-distance="10"
            searchable
            can-deselect
            @load-more="getMore"
            @search="search = $event"
          />
        </div>
      </BaseFieldGroup>
    </BaseSectionBody>
  </BaseSection>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { WdsSelect, WdsAlert, WdsInputCurrency } from '@worksome/wds-components'
import gql from 'graphql-tag'

import { flat } from '@/utils/tools'
import BaseSection from '@/components/BaseSection.vue'
import BaseFieldGroup from '@/components/BaseFieldGroup.vue'
import BaseSectionBody from '@/components/BaseSectionBody.vue'
import BaseSectionHeader from '@/components/BaseSectionHeader.vue'
import { $t } from '@/composables/translation'

import { getErrorMessage } from '@/components/Job/functions/getErrorMessage'
import { useJobFormStore } from '@/components/Job/Edit/store/jobFormStore'
import { useValidationStore } from '@/components/Job/Edit/store/useValidationStore'

const store = useJobFormStore()
const validationStore = useValidationStore()

const props = withDefaults(
  defineProps<{
    inputCurrencyConfig: {
      currencySymbol?: string
      thousandsSeparator: string
      decimalSeparator: string
      showFractions: boolean
    }
    projectsEnabled?: boolean
    requiredJobBudget?: boolean
  }>(),
  {
    projectsEnabled: false,
    requiredJobBudget: false,
  },
)

const search = ref<string>('')

const { result, loading, fetchMore } = useQuery(
  gql`
    query Projects($accounts: [ID!], $page: Int, $count: Int, $search: String) {
      projects(accounts: $accounts, first: $count, page: $page, search: $search) {
        data {
          id
          name
        }
      }
    }
  `,
  {
    accounts: [currentAccount.globalId],
    count: 50,
    page: 1,
    search,
  },
  {
    clientId: 'publicApiCustomFields',
    debounce: 10,
    enabled: props.projectsEnabled,
  },
)

const projects = computed(() =>
  result.value?.projects.data.map((project) => ({ label: project.name, value: project.id })),
)

const selectedProject = computed({
  get: () => ({ value: store.form.project?.id }),
  set: (selected) => {
    store.form.project = { id: (selected as { value: string })?.value }
  },
})

async function getMore() {
  if (!loading.value) {
    // This logic prevents accidentally loading in an old page of data resulting in duplicated data
    const { hasMorePages, currentPage } = flat(result.value ?? {}) ?? {}
    if (!hasMorePages) return

    fetchMore({
      variables: {
        page: currentPage + 1,
      },
    })
  }
}
</script>
